import React, { useEffect, useState } from 'react';
import { RefreshCw, QrCode, ScanLine, Store, Truck, ChevronDown, ChevronUp, Trash2, FileDown, CheckCircle2, Circle } from 'lucide-react';
import { vendorAdminApi, orderQrApi } from '../services/api';
import { useVendorContext } from './VendorAdminLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import QrPickupScanner from '../driver/QrPickupScanner';
import OrderStatusToggles from '../components/OrderStatusToggles';
import ReassignDriverButton from './ReassignDriverButton';
import { formatMoney } from '../lib/money';
import ConfirmPaymentButton from '../components/ConfirmPaymentButton';
import ETransferProofView from '../components/ETransferProofView';
import { CustomerInvoiceTile, PickingListTile } from './QrSidePanels';
import api from '../services/api';
import { toast } from 'sonner';

const STATUSES = ['all', 'placed', 'payment_confirmed', 'preparing', 'ready', 'on_the_way', 'delivered', 'cancelled'];
const STATUS_COLORS = {
  placed: 'bg-slate-100 text-slate-700',
  payment_confirmed: 'bg-cyan-50 text-cyan-700',
  confirmed: 'bg-blue-50 text-blue-700',
  preparing: 'bg-amber-50 text-amber-700',
  ready: 'bg-violet-50 text-violet-700',
  ready_for_pickup: 'bg-violet-50 text-violet-700',
  on_the_way: 'bg-emerald-50 text-emerald-700',
  delivered: 'bg-slate-100 text-slate-500',
  cancelled: 'bg-rose-50 text-rose-700',
};

// Next-status quick actions per current status (vendor-side workflow)
const VENDOR_NEXT_ACTIONS = {
  placed: [{ status: 'payment_confirmed', label: 'Payment confirmed' }],
  payment_confirmed: [{ status: 'preparing', label: 'Start preparing' }],
  confirmed: [{ status: 'preparing', label: 'Start preparing' }],
  preparing: [{ status: 'ready', label: 'Order is ready' }],
};

const PickingList = ({ order, onPick }) => {
  if (order.status !== 'preparing' || !(order.picking_list || []).length) return null;
  const pl = order.picking_list;
  const done = pl.filter((x) => x.picked).length;
  return (
    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50/60 p-2" data-testid={`picking-list-${order.id}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800 mb-1">
        Picking list · {done}/{pl.length} picked
      </div>
      <div className="space-y-1">
        {pl.map((it, idx) => (
          <div key={it.item_id || idx} className="flex items-center gap-2 text-xs">
            {it.picked
              ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              : <Circle className="h-3.5 w-3.5 text-slate-300 shrink-0" />}
            <span className={`flex-1 truncate ${it.picked ? 'line-through text-slate-400' : 'text-slate-700'}`}>{it.qty}× {it.name}</span>
            <button
              onClick={() => onPick(order.id, it, idx)}
              className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition-colors ${it.picked ? 'bg-white text-slate-500 border-slate-200 hover:border-slate-300' : 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'}`}
              data-testid={`pick-item-${order.id}-${idx}`}
            >
              {it.picked ? 'UNDO' : 'PICKED'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const VendorOrdersPage = () => {
  const { vendor_id, impersonating } = useVendorContext();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [qr, setQr] = useState(null); // { id, data_url, etransfer?, cogs?, currency? }
  const [scanOrderId, setScanOrderId] = useState(null);
  const [expanded, setExpanded] = useState(new Set()); // order ids with toggles panel expanded // id being verified via QR scanner
  const [qrPanel, setQrPanel] = useState(null); // null | 'invoice' | 'picking' — expanded tile in the QR dialog

  const showQr = async (id) => {
    try {
      const data = await orderQrApi.get(id);
      const order = orders.find((o) => o.id === id);
      const cogs = Number(order?.goods_invoice_amount ?? order?.vendor_amount ?? ((Number(order?.subtotal) || 0) + (Number(order?.tax_amount) || 0)));
      // Best-effort fetch of the customer's e-transfer proof for the goods
      // invoice — shown side-by-side with the QR so the vendor can verify
      // amount + confirmation reference before releasing the order.
      let etransfer = null;
      try {
        const proofs = await vendorAdminApi.getOrderETransfer(id, impersonating ? vendor_id : undefined);
        etransfer = (proofs?.submissions || [])[0] || null;
      } catch (e) { /* proofs not available — leave null */ }
      setQr({ id, data_url: data.data_url, etransfer, cogs, currency: order?.currency || 'CAD' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load QR'); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await vendorAdminApi.orders({ status_f: filter === 'all' ? undefined : filter }, impersonating ? vendor_id : undefined);
      setOrders(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, vendor_id]);

  const changeStatus = async (id, status) => {
    try {
      const res = await vendorAdminApi.updateOrderStatus(id, status, impersonating ? vendor_id : undefined);
      setOrders((prev) => prev.map((o) => (o.id === id
        ? { ...o, status, ...(res?.picking_list ? { picking_list: res.picking_list } : {}) }
        : o)));
      toast.success(`Order ${id} → ${status.replace(/_/g, ' ')}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const pickItem = async (id, it, idx) => {
    try {
      const res = await vendorAdminApi.pickOrderItem(
        id, { item_id: it.item_id, index: idx, picked: !it.picked },
        impersonating ? vendor_id : undefined);
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, picking_list: res.picking_list } : o)));
      if (res.all_picked) toast.success('All items picked — you can now mark the order READY');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const confirmGoodsPayment = async (id, method, reference) => {
    const resp = await vendorAdminApi.confirmGoodsPayment(id, method, reference, impersonating ? vendor_id : undefined);
    setOrders((prev) => prev.map((o) => (o.id === id ? {
      ...o,
      goods_invoice_status: resp.goods_invoice_status,
      platform_invoice_status: resp.platform_invoice_status,
      assignment_status: resp.assignment_status,
    } : o)));
    return resp;
  };

  const downloadInvoicePdf = async (id) => {
    try {
      const params = impersonating ? { vendor_id } : undefined;
      const r = await api.get(`/vendor-admin/orders/${id}/invoice.pdf`, { params, responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `invoice-${id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not download invoice');
    }
  };

  const verifyPickup = async (id, token) => {
    return vendorAdminApi.confirmPickup(id, token, impersonating ? vendor_id : undefined);
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete order ${id}?\n\nThis is irreversible and also removes its payment/transaction records.`)) return;
    try {
      await vendorAdminApi.deleteOrder(id, impersonating ? vendor_id : undefined);
      setOrders((prev) => prev.filter((o) => o.id !== id));
      toast.success(`Order ${id} deleted`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900">Orders</h1>
          <p className="text-slate-500 mt-1 text-sm">Manage incoming orders for your store.</p>
          <p className="text-[11px] text-slate-400 mt-0.5" data-testid="vendor-orders-amount-note">
            <b>Your invoice</b> = goods + tax (COGS) — this is what you receive. The customer&rsquo;s checkout total is higher because it also includes ShipitNow delivery &amp; service fees, billed on a separate ShipitNow invoice.
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="flex-1 sm:flex-none sm:w-44 h-10" data-testid="vendor-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s === 'all' ? 'All statuses' : s.replace(/_/g, ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="vendor-orders-refresh">
            <RefreshCw className={`h-4 w-4 sm:mr-1.5 ${loading ? 'animate-spin' : ''}`} /><span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Mobile card list — under md */}
      <div className="mt-4 space-y-2.5 md:hidden" data-testid="vendor-orders-mobile-list">
        {orders.length === 0 ? (
          <div className="text-center text-slate-500 py-10 bg-white border border-slate-200 rounded-2xl">No orders.</div>
        ) : orders.map((o) => {
          const isPickup = (o.fulfillment_type || 'delivery') === 'pickup';
          const isDelivered = o.status === 'delivered';
          return (
            <div key={o.id} className="bg-white border border-slate-200 rounded-2xl p-3.5" data-testid={`vendor-order-card-${o.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono text-[11px] text-slate-500 truncate">{o.id}</div>
                  {o.pay_for_me && (
                    <div className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[9px] font-bold uppercase tracking-wide mt-0.5">
                      Pay for me · driver pays at pickup
                    </div>
                  )}
                  <div className="text-sm font-semibold text-slate-900 mt-0.5" data-testid={`vendor-order-payout-mobile-${o.id}`}>{formatMoney(Number(o.vendor_amount ?? ((Number(o.subtotal)||0) + (Number(o.tax_amount)||0))) || 0, o.currency)} <span className="font-normal text-[10px] text-slate-400">your invoice</span></div>
                  <div className="text-[10px] text-slate-500 mt-0.5">Goods {formatMoney(Number(o.subtotal)||0, o.currency)} · Tax {formatMoney(Number(o.tax_amount)||0, o.currency)} · Customer total {formatMoney(Number(o.total)||0, o.currency)} (incl. ShipitNow fees)</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{o.placedAt ? new Date(o.placedAt).toLocaleString() : '—'}</div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {isPickup ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-[10px] font-medium">
                      <Store className="h-3 w-3" /> Pickup
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-[10px] font-medium">
                      <Truck className="h-3 w-3" /> Delivery
                    </span>
                  )}
                  <Badge className={`${STATUS_COLORS[o.status] || 'bg-slate-100 text-slate-700'} border-0 hover:opacity-100 capitalize text-[10px]`}>{(o.status || 'placed').replace(/_/g, ' ')}</Badge>
                </div>
              </div>
              <div className="mt-2 text-xs text-slate-600">
                {o.items.slice(0, 3).map((i) => (
                  <div key={i.id} className="truncate"><span className="text-slate-900 font-medium">{i.qty}×</span> {i.name}</div>
                ))}
                {o.items.length > 3 && <div className="text-slate-400 text-[11px]">+{o.items.length - 3} more</div>}
              </div>
              <PickingList order={o} onPick={pickItem} />
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(VENDOR_NEXT_ACTIONS[o.status] || []).map((act) => (
                  <Button
                    key={act.status}
                    variant="default" size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 min-w-[calc(50%-6px)]"
                    onClick={() => changeStatus(o.id, act.status)}
                    data-testid={`vendor-action-mobile-${o.id}-${act.status}`}
                  >
                    {act.label}
                  </Button>
                ))}
                <Button variant="outline" size="sm" onClick={() => showQr(o.id)} title="Show pickup QR" data-testid={`vendor-qr-btn-mobile-${o.id}`}>
                  <QrCode className="h-4 w-4" />
                </Button>
                {isPickup && !isDelivered && (
                  <Button
                    variant="default" size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => setScanOrderId(o.id)}
                    data-testid={`confirm-pickup-mobile-${o.id}`}
                  >
                    <ScanLine className="h-4 w-4 mr-1" /> Confirm pickup
                  </Button>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid={`vendor-status-select-mobile-${o.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.filter((s) => s !== 'all').map((s) => (
                      <SelectItem key={s} value={s} className="capitalize text-xs">{s.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => remove(o.id)}
                  className="h-8 px-2 text-rose-600 hover:text-rose-700 border-rose-200 hover:bg-rose-50"
                  data-testid={`vendor-delete-order-mobile-${o.id}`}
                  title="Delete this order"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              {/* Full pipeline toggles (mobile card) */}
              <button
                onClick={() => setExpanded((prev) => { const n = new Set(prev); n.has(o.id) ? n.delete(o.id) : n.add(o.id); return n; })}
                className="mt-2 w-full text-[11px] font-medium text-slate-500 hover:text-emerald-700 flex items-center justify-center gap-1 py-1"
                data-testid={`toggle-panel-open-mobile-${o.id}`}
              >
                {expanded.has(o.id) ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                {expanded.has(o.id) ? 'Hide pipeline toggles' : 'Show pipeline toggles'}
              </button>
              {expanded.has(o.id) && (
                <OrderStatusToggles
                  role="vendor"
                  orderId={o.id}
                  currentStatus={o.status}
                  onChanged={(newStatus) => setOrders((all) => all.map((row) => (row.id === o.id ? { ...row, status: newStatus } : row)))}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop table — md and up */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left font-medium px-4 py-3">Order</th>
                <th className="text-left font-medium px-4 py-3">Type</th>
                <th className="text-left font-medium px-4 py-3">Items</th>
                <th className="text-right font-medium px-4 py-3">Goods</th>
                <th className="text-right font-medium px-4 py-3">Tax</th>
                <th className="text-right font-medium px-4 py-3" title="Your invoice = goods + tax (COGS). Customer total also includes ShipitNow delivery + service fees.">Your invoice</th>
                <th className="text-right font-medium px-4 py-3 text-slate-400" title="What the customer paid at checkout, including ShipitNow delivery + service fees.">Customer total</th>
                <th className="text-left font-medium px-4 py-3">Placed</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-right font-medium px-4 py-3">Update</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.length === 0 ? (
                <tr><td colSpan="10" className="text-center text-slate-500 py-10">No orders.</td></tr>
              ) : orders.map((o) => {
                const isPickup = (o.fulfillment_type || 'delivery') === 'pickup';
                const isDelivered = o.status === 'delivered';
                return (
                <React.Fragment key={o.id}>
                <tr className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">
                    {o.id}
                    {o.pay_for_me && (
                      <div className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[9px] font-bold uppercase tracking-wide" data-testid={`payforme-badge-${o.id}`}>
                        Pay for me · driver pays at pickup
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isPickup ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-xs font-medium" data-testid={`type-pickup-${o.id}`}>
                        <Store className="h-3 w-3" /> Pickup
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-xs font-medium" data-testid={`type-delivery-${o.id}`}>
                        <Truck className="h-3 w-3" /> Delivery
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    <div className="max-w-sm">
                      {o.items.map((i) => (
                        <div key={i.id} className="truncate"><span className="text-slate-900 font-medium">{i.qty}×</span> {i.name}</div>
                      ))}
                      <PickingList order={o} onPick={pickItem} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700" data-testid={`vendor-order-cogs-${o.id}`}>{formatMoney(Number(o.subtotal)||0, o.currency)}</td>
                  <td className="px-4 py-3 text-right text-slate-700" data-testid={`vendor-order-tax-${o.id}`}>{formatMoney(Number(o.tax_amount)||0, o.currency)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900" data-testid={`vendor-order-payout-${o.id}`}>{formatMoney(Number(o.vendor_amount ?? ((Number(o.subtotal)||0) + (Number(o.tax_amount)||0))) || 0, o.currency)}</td>
                  <td className="px-4 py-3 text-right text-slate-400" data-testid={`vendor-order-customer-total-${o.id}`} title="Includes ShipitNow delivery + service fees — billed separately by ShipitNow, not part of your invoice.">{formatMoney(Number(o.total) || 0, o.currency)}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{o.placedAt ? new Date(o.placedAt).toLocaleString() : '—'}</td>
                  <td className="px-4 py-3">
                    <Badge className={`${STATUS_COLORS[o.status] || 'bg-slate-100 text-slate-700'} border-0 hover:opacity-100 capitalize`}>{(o.status || 'placed').replace(/_/g, ' ')}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-2 justify-end flex-wrap">
                      <ConfirmPaymentButton
                        kind="goods"
                        orderId={o.id}
                        amount={Number(o.goods_invoice_amount ?? o.vendor_amount ?? ((Number(o.subtotal)||0) + (Number(o.tax_amount)||0)))}
                        currency={o.currency || 'CAD'}
                        status={o.goods_invoice_status || 'pending'}
                        onConfirm={({ method, reference }) => confirmGoodsPayment(o.id, method, reference)}
                        buttonSize="sm"
                      />
                      {(VENDOR_NEXT_ACTIONS[o.status] || []).map((act) => (
                        <Button
                          key={act.status}
                          variant="default" size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => changeStatus(o.id, act.status)}
                          data-testid={`vendor-action-${o.id}-${act.status}`}
                        >
                          {act.label}
                        </Button>
                      ))}
                      <Button variant="outline" size="sm" onClick={() => showQr(o.id)} title="Show pickup QR" data-testid={`vendor-qr-btn-${o.id}`}>
                        <QrCode className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => downloadInvoicePdf(o.id)} title="Download invoice PDF" data-testid={`vendor-invoice-pdf-${o.id}`}>
                        <FileDown className="h-4 w-4" />
                      </Button>
                      {!['delivered', 'cancelled', 'refunded', 'failed'].includes(o.status) && (
                        <ReassignDriverButton
                          orderId={o.id}
                          currentDriverId={o.driver_id}
                          currentDriverName={o.driver_name}
                          impersonating={impersonating}
                          vendorId={vendor_id}
                          onReassigned={() => load()}
                        />
                      )}
                      {isPickup && !isDelivered && (
                        <Button
                          variant="default"
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => setScanOrderId(o.id)}
                          title="Scan customer's QR to confirm pickup"
                          data-testid={`confirm-pickup-btn-${o.id}`}
                        >
                          <ScanLine className="h-4 w-4 mr-1" /> Confirm pickup
                        </Button>
                      )}
                      <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                        <SelectTrigger className="w-40 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.filter((s) => s !== 'all').map((s) => (
                            <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => remove(o.id)}
                        className="text-rose-600 hover:text-rose-700 border-rose-200 hover:bg-rose-50"
                        data-testid={`vendor-delete-order-${o.id}`}
                        title="Delete this order"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => setExpanded((prev) => { const n = new Set(prev); n.has(o.id) ? n.delete(o.id) : n.add(o.id); return n; })}
                        className="text-slate-500 hover:text-emerald-700"
                        data-testid={`toggle-panel-open-${o.id}`}
                        title={expanded.has(o.id) ? 'Hide pipeline toggles' : 'Show pipeline toggles'}
                      >
                        {expanded.has(o.id) ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </div>
                  </td>
                </tr>
                {expanded.has(o.id) && (
                  <tr key={`${o.id}-toggles`}>
                    <td colSpan="10" className="px-4 pb-4 bg-slate-50/60">
                      <div className="max-w-xl">
                        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Full pipeline toggles</div>
                        <OrderStatusToggles
                          role="vendor"
                          orderId={o.id}
                          currentStatus={o.status}
                          onChanged={(newStatus) => setOrders((all) => all.map((row) => (row.id === o.id ? { ...row, status: newStatus } : row)))}
                        />
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>);})}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!qr} onOpenChange={(o) => { if (!o) { setQr(null); setQrPanel(null); } }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode className="h-5 w-5 text-emerald-600" />Order QR — {qr?.id}</DialogTitle></DialogHeader>
          {qr?.data_url && (() => {
            const qrOrder = orders.find((o) => o.id === qr.id) || {};
            return (
            <div className="grid gap-4 md:grid-cols-2 pt-2">
              <div className="flex flex-col items-center gap-2">
                <img src={qr.data_url} alt="QR" className="h-72 w-72 max-w-full border border-slate-200 rounded-lg" data-testid="vendor-qr-image" />
                <p className="text-xs text-slate-500 text-center">Hold the phone up so the driver can scan this — or ask the driver to show theirs and tap &ldquo;Confirm pickup&rdquo; to scan it.</p>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <CustomerInvoiceTile
                    order={qrOrder}
                    cogs={qr.cogs}
                    currency={qr.currency || 'CAD'}
                    expanded={qrPanel === 'invoice'}
                    onToggle={() => setQrPanel((p) => (p === 'invoice' ? null : 'invoice'))}
                  />
                  <PickingListTile
                    order={qrOrder}
                    onPick={(it, idx) => pickItem(qr.id, it, idx)}
                    onReady={() => { changeStatus(qr.id, 'ready'); setQrPanel(null); }}
                    expanded={qrPanel === 'picking'}
                    onToggle={() => setQrPanel((p) => (p === 'picking' ? null : 'picking'))}
                  />
                </div>
                <ETransferProofView
                  submission={qr.etransfer}
                  amountExpected={qr.cogs}
                  testidPrefix="vendor-etransfer"
                />
                <p className="text-[10px] text-slate-400">
                  Delivery &amp; service fees are billed on a separate ShipitNow invoice and approved by ShipitNow admin.
                </p>
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {scanOrderId && (
        <QrPickupScanner
          orderId={scanOrderId}
          open={!!scanOrderId}
          onClose={() => setScanOrderId(null)}
          onSuccess={() => {
            setOrders((prev) => prev.map((o) => (o.id === scanOrderId ? { ...o, status: 'delivered' } : o)));
            toast.success('Pickup confirmed!');
          }}
          verifyFn={verifyPickup}
          title="Scan customer's QR"
          helpText="Ask the customer to show their pickup QR, then scan it to release the order."
        />
      )}
    </div>
  );
};

export default VendorOrdersPage;
